import {
  Box,
  Flex,
  Link,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { useListUsers } from '../../../../../../../admin/queries';
import {
  GET_DETECTIONS,
  QUERY_DETECTIONS_PAGINATED,
  useAssignDetections,
} from '../../../../../../queries';

import { useSearchParams } from 'react-router';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import { useContext, useState } from 'react';
import { MAX_ASSIGNEE_LENGTH } from '@investigator/shared/constants';
import {
  DetectionQueryVariables,
  SantizedDetection,
} from '../../../../../../pages';
import { filterAssignableUsers } from '@investigator/shared/utils';
import { DetectionAssigneePopover } from '../../../../../DetectionAssigneePopover';
import { IGetDetectionVariables } from '../../SummaryPanel';
import {
  Detection,
  DetectionAssignmentInfo,
} from '@investigator/shared/generated/graphql';

interface DetectionAssigneeProps {
  refetchMain?: boolean;
  detectionInfo?: Detection | SantizedDetection;
  assignmentInfo?: DetectionAssignmentInfo;
  getDetectionVariables?: IGetDetectionVariables;
  queryDetectionsPaginatedVariables?: DetectionQueryVariables;
}

export const DetectionAssignee = ({
  refetchMain,
  detectionInfo,
  assignmentInfo,
  getDetectionVariables,
  queryDetectionsPaginatedVariables,
}: DetectionAssigneeProps) => {
  const showToastMessage = useToast();

  const { user, userRole } = useContext(AuthContext);

  const [_, setSearchParams] = useSearchParams();

  const { controls, detection, toasts } = i18n.pages.detections;

  const { assignToMe } = controls;

  const { unassigned, externalTenant } = detection;

  const [isAssigneeHovered, setIsAssigneeHovered] = useState<boolean>(false);

  const { loading: listUsersLoading, data: { listUsers = [] } = {} } =
    useListUsers({
      skip: !user?.attributes?.['custom:tenant_id'],
      variables: {
        tenants: [`${user?.attributes?.['custom:tenant_id']}`],
        groups: ['admin', 'analyst'],
      },
    });

  const assignableUsers = filterAssignableUsers({ user, users: listUsers });

  const currentAssignee = assignmentInfo?.assigned_to_user_alias
    ? assignmentInfo.assigned_to_user_alias.slice(0, MAX_ASSIGNEE_LENGTH)
    : unassigned;

  const isActiveUserAssignable =
    assignableUsers.some(
      (assignableUser) => assignableUser?.value === user?.username,
    ) && user?.username !== assignmentInfo?.assigned_to_username;

  const refetchQueries = [
    {
      query: GET_DETECTIONS,
      variables: getDetectionVariables,
    },
  ];

  const setUpdateDetectionsParam = () => {
    if (refetchMain) {
      setSearchParams((params) => {
        params.set('refetch_main', 'true');
        return params;
      });
    }
  };

  const [assignDetections, { loading: assignLoading }] = useAssignDetections({
    awaitRefetchQueries: true,
    refetchQueries: !queryDetectionsPaginatedVariables
      ? refetchQueries
      : [
          ...refetchQueries,
          {
            query: QUERY_DETECTIONS_PAGINATED,
            variables: queryDetectionsPaginatedVariables,
          },
        ],
    onCompleted: () => {
      setUpdateDetectionsParam();
      showToastMessage({
        status: 'success',
        title: toasts.success,
        description: toasts.assigneeSet,
        isClosable: true,
        position: 'bottom-right',
      });
    },
    onError: () => {
      showToastMessage({
        status: 'error',
        title: toasts.error,
        description: toasts.assigneeNotSet,
        isClosable: true,
        position: 'bottom-right',
      });
    },
  });

  const handleAssignDetections = () => {
    assignDetections({
      variables: {
        items: [
          {
            assignment_info: {
              assigned_to_username: user?.username || null,
            },
            update_info: {
              last_updated_timestamp:
                detectionInfo?.update_info?.last_updated_timestamp,
            },
            detection_id: detectionInfo?.detection_id,
            tenant: user?.attributes?.['custom:tenant_id'],
          },
        ],
      },
    });
  };

  return userRole === 'viewer' ? (
    <Tag
      mr={2}
      size="md"
      variant="solid"
      data-testid="detection-alert-assignee"
      colorScheme={assignmentInfo?.assigned_to_user_alias ? 'purple' : 'gray'}
    >
      {!assignmentInfo?.assigned_to_user_alias
        ? unassigned
        : assignmentInfo?.assigned_to_user_alias}
    </Tag>
  ) : (
    <Flex
      w="100%"
      maxW="254px"
      zIndex={0}
      p="2px 4px"
      minH="28px"
      h="auto"
      borderRadius="2px"
      alignItems="flex-start"
      position="relative"
      wordBreak="break-all"
      transition="all .1s ease-out"
      data-testid="summary-panel-inline-edit-assignee"
      onMouseOver={() => setIsAssigneeHovered(true)}
      onMouseLeave={() => setIsAssigneeHovered(false)}
      _hover={{
        backgroundColor: 'whiteAlpha.50',
      }}
      wrap="wrap"
      gap={2}
    >
      <Flex
        flex="1"
        minW="0"
        wrap="wrap"
        direction={{ base: 'column', sm: 'row' }}
        align="flex-start"
        gap={2}
        data-testid="entity-panel-source-value"
      >
        <Tag
          size="md"
          variant="solid"
          data-testid="detection-alert-assignee"
          colorScheme={
            assignmentInfo?.assigned_to_user_alias ? 'purple' : 'gray'
          }
          maxW="100%"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          mr={2}
        >
          <Tooltip
            label={
              assignmentInfo?.assigned_to_user_alias === 'External User'
                ? externalTenant
                : ''
            }
          >
            <Text isTruncated>{currentAssignee}</Text>
          </Tooltip>
        </Tag>

        {assignLoading || listUsersLoading ? (
          <Spinner size="xs" mr={2} />
        ) : (
          isActiveUserAssignable && (
            <Link
              zIndex={1}
              color="text.link"
              position="relative"
              minW="83px"
              whiteSpace="nowrap"
              onClick={handleAssignDetections}
              data-testid="assign-detection-to-me-link"
              textStyle="body-md"
              lineHeight="20px"
              mr={2}
            >
              {assignToMe}
            </Link>
          )
        )}
      </Flex>

      <Box
        w="24px"
        h="24px"
        minW="24px"
        minH="24px"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        visibility={isAssigneeHovered ? 'visible' : 'hidden'}
        pointerEvents={isAssigneeHovered ? 'auto' : 'none'}
        flexShrink={0}
      >
        <DetectionAssigneePopover
          setUpdateDetectionsParam={setUpdateDetectionsParam}
          isAssigneeHovered={isAssigneeHovered}
          getDetectionVariables={getDetectionVariables}
          queryDetectionsPaginatedVariables={queryDetectionsPaginatedVariables}
        />
      </Box>
    </Flex>
  );
};
